import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Card,
  CardContent,
  CardMedia,
  Divider,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import axios from 'axios';
import ResendOfferForm from './ResendOfferForm';

const ResendOfferTable = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});
  const [openDialog, setOpenDialog] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const resellerId = localStorage.getItem('resellerId');

  const fetchOffers = async () => {
    try {
      const response = await axios.get('https://reviewhub-lc.onrender.com/api/resendoffers', {
        params: { resellerId },
      });
      setOffers(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching offers:', err);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (resellerId) {
      fetchOffers();
    } else {
      console.warn('Reseller ID not found in localStorage');
      setLoading(false);
    }
  }, [resellerId]);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this offer?')) {
      try {
        await axios.delete(`https://reviewhub-lc.onrender.com/api/resendoffers/${id}`);
        setOffers((prev) => prev.filter((offer) => offer._id !== id));
      } catch (err) {
        console.error('Error deleting offer:', err);
      }
    }
  };

  const handleEditClick = (offer) => {
    setEditId(offer._id);
    setEditData({
      originalPrice: offer.originalPrice,
      offerPrice: offer.offerPrice,
      endDateTime: offer.endDateTime
    });
  };

  const handleCancelEdit = () => {
    setEditId(null);
    setEditData({});
  };

  const handleChange = (field, value) => {
    setEditData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      await axios.put(`https://reviewhub-lc.onrender.com/api/resendoffers/${editId}`, editData);
      setOffers((prev) =>
        prev.map((item) => (item._id === editId ? { ...item, ...editData } : item))
      );
      setEditId(null);
      setEditData({});
    } catch (err) {
      console.error('Error updating offer:', err);
    }
  };

  const handleDialogOpen = () => setOpenDialog(true);
  const handleDialogClose = () => {
    setOpenDialog(false);
    fetchOffers();
  };

  if (loading) {
    return (
      <Box textAlign="center" mt={5}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Resend Offers</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleDialogOpen} sx={{ backgroundColor: '#616161' }}>
          Add Offer
        </Button>
      </Stack>

      {offers.length === 0 ? (
        <Typography>No offers found.</Typography>
      ) : isMobile ? (
        // 📱 Mobile Card Layout with Edit Support
        <Stack spacing={2}>
          {offers.map((offer) => (
            <Card key={offer._id} variant="outlined">
              {offer.productImage && (
                <CardMedia
                  component="img"
                  height="140"
                  image={offer.productImage}
                  alt={offer.productName}
                />
              )}
              <CardContent>
                <Typography variant="h6">{offer.productName}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Platform: {offer.platform}
                </Typography>

                {editId === offer._id ? (
                  <>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Original Price"
                      value={editData.originalPrice}
                      onChange={(e) => handleChange('originalPrice', e.target.value)}
                      sx={{ mb: 1, mt: 1 }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Offer Price"
                      value={editData.offerPrice}
                      onChange={(e) => handleChange('offerPrice', e.target.value)}
                      sx={{ mb: 1 }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      type="datetime-local"
                      label="End Date & Time"
                      value={editData.endDateTime ? new Date(editData.endDateTime).toISOString().slice(0, 16) : ''}
                      onChange={(e) => handleChange('endDateTime', e.target.value)}
                      sx={{ mb: 1 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </>
                ) : (
                  <>
                    <Typography>Original: ₹{offer.originalPrice}</Typography>
                    <Typography>Offer: ₹{offer.offerPrice}</Typography>
                    <Typography>
                      End: {new Date(offer.endDateTime).toLocaleString()}
                    </Typography>
                  </>
                )}

                <Typography>
                  Start: {new Date(offer.startDateTime).toLocaleString()}
                </Typography>
                <Typography
                  color={new Date() > new Date(offer.endDateTime) ? 'error' : 'green'}
                >
                  {new Date() > new Date(offer.endDateTime) ? 'Expired' : 'Active'}
                </Typography>

                <Divider sx={{ my: 1 }} />

                <Stack direction="row" spacing={1}>
                  {editId === offer._id ? (
                    <>
                      <IconButton color="success" onClick={handleSave}>
                        <SaveIcon />
                      </IconButton>
                      <IconButton color="warning" onClick={handleCancelEdit}>
                        <CloseIcon />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <IconButton color="primary" onClick={() => handleEditClick(offer)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton color="error" onClick={() => handleDelete(offer._id)}>
                        <DeleteIcon />
                      </IconButton>
                    </>
                  )}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        // 💻 Desktop Table Layout
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
              <TableRow>
                <TableCell>Image</TableCell>
                <TableCell>Product Name</TableCell>
                <TableCell>Platform</TableCell>
                <TableCell>Original Price</TableCell>
                <TableCell>Offer Price</TableCell>
                <TableCell>Start</TableCell>
                <TableCell>End</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {offers.map((offer) => (
                <TableRow key={offer._id}>
                  <TableCell>
                    {offer.productImage ? (
                      <img
                        src={offer.productImage}
                        alt={offer.productName}
                        width="60"
                        height="60"
                        style={{ borderRadius: 4 }}
                      />
                    ) : (
                      'N/A'
                    )}
                  </TableCell>
                  <TableCell>{offer.productName}</TableCell>
                  <TableCell>{offer.platform}</TableCell>
                  <TableCell>
                    {editId === offer._id ? (
                      <TextField
                        size="small"
                        type="number"
                        value={editData.originalPrice}
                        onChange={(e) => handleChange('originalPrice', e.target.value)}
                      />
                    ) : (
                      `₹${offer.originalPrice}`
                    )}
                  </TableCell>
                  <TableCell>
                    {editId === offer._id ? (
                      <TextField
                        size="small"
                        type="number"
                        value={editData.offerPrice}
                        onChange={(e) => handleChange('offerPrice', e.target.value)}
                      />
                    ) : (
                      `₹${offer.offerPrice}`
                    )}
                  </TableCell>
                  <TableCell>{new Date(offer.startDateTime).toLocaleString()}</TableCell>
                  <TableCell>
                    {editId === offer._id ? (
                      <TextField
                        size="small"
                        type="datetime-local"
                        value={editData.endDateTime ? new Date(editData.endDateTime).toISOString().slice(0, 16) : ''}
                        onChange={(e) => handleChange('endDateTime', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    ) : (
                      new Date(offer.endDateTime).toLocaleString()
                    )}
                  </TableCell>
                  <TableCell>
                    {new Date() > new Date(offer.endDateTime) ? (
                      <Typography color="error">Expired</Typography>
                    ) : (
                      <Typography color="green">Active</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {editId === offer._id ? (
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <IconButton color="success" onClick={handleSave}>
                          <SaveIcon />
                        </IconButton>
                        <IconButton color="warning" onClick={handleCancelEdit}>
                          <CloseIcon />
                        </IconButton>
                      </Stack>
                    ) : (
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <IconButton color="primary" onClick={() => handleEditClick(offer)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton color="error" onClick={() => handleDelete(offer._id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Resend Offer Form</DialogTitle>
        <DialogContent>
          <ResendOfferForm onClose={handleDialogClose} resellerId={resellerId} />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ResendOfferTable;
